'use strict';

import { CONFIG } from '../core/config.js';

/**
 * Form Synchronization Manager
 * Handles multi-form synchronization, dropdown label updates, and checkbox/radio state management
 */
export class FormSyncManager {
	constructor(forms, validationManager, callbacks = {}) {
		this.forms = forms;
		this.validationManager = validationManager;
		this.callbacks = {
			applyCrossFiltering: callbacks.applyCrossFiltering || (() => {}),
			resetCrossFiltering: callbacks.resetCrossFiltering || (() => {}),
		};
	}

	/**
	 * Sync checkbox selection to all forms
	 * Handles both regular checkboxes and single-select checkboxes in dropdowns
	 * @param {HTMLInputElement} sourceCheckbox - The checkbox input that was selected
	 */
	syncCheckboxToAllForms(sourceCheckbox) {
		if (!this.forms || this.forms.length === 0) return;

		const { name, value } = sourceCheckbox;
		this.callbacks.applyCrossFiltering();

		this.forms.forEach((form) => {
			const targetCheckbox = form.querySelector(
				`input[type="checkbox"][name="${name}"][value="${value}"]`,
			);
			if (targetCheckbox && !targetCheckbox.checked) {
				targetCheckbox.checked = true;

				const option = targetCheckbox.closest(CONFIG.ELEMENTS.option);
				if (
					option &&
					targetCheckbox.matches(CONFIG.ELEMENTS.checkboxSingleSelect)
				) {
					option.classList.add(CONFIG.CSS_CLASSES.optionSelected);
				}

				const dropdown = targetCheckbox.closest(CONFIG.ELEMENTS.dropdown);
				if (dropdown && dropdown.querySelector('[data-required="true"]')) {
					this.validationManager.validateField(dropdown);
				}

				this.updateDropdownLabel(targetCheckbox);
			} else if (targetCheckbox && targetCheckbox.checked) {
				this.updateDropdownLabel(targetCheckbox);
			}
		});
	}

	/**
	 * Sync checkbox deselection to all forms
	 * @param {HTMLInputElement} sourceCheckbox - The checkbox input that was deselected
	 */
	syncCheckboxDeselectionToAllForms(sourceCheckbox) {
		if (!this.forms || this.forms.length === 0) return;

		const { name, value } = sourceCheckbox;

		this.forms.forEach((form) => {
			const targetCheckbox = form.querySelector(
				`input[name="${name}"][value="${value}"]`,
			);
			if (targetCheckbox && targetCheckbox.checked) {
				targetCheckbox.checked = false;

				const option = targetCheckbox.closest(CONFIG.ELEMENTS.option);
				if (
					option &&
					targetCheckbox.matches(CONFIG.ELEMENTS.checkboxSingleSelect)
				) {
					option.classList.remove(CONFIG.CSS_CLASSES.optionSelected);
				}

				this.updateDropdownLabel(targetCheckbox);
				this.callbacks.resetCrossFiltering(targetCheckbox);
			}
		});
	}

	/**
	 * Sync radio button selection to all forms
	 * Handles Type of Care tabbed radio inputs and any other radio button fields
	 * @param {HTMLInputElement} sourceRadio - The radio input that was selected
	 */
	syncRadioToAllForms(sourceRadio) {
		if (!this.forms || this.forms.length === 0) return;

		const { name, value } = sourceRadio;
		this.callbacks.applyCrossFiltering();

		this.forms.forEach((form) => {
			// Find all radio inputs with the same name in this form
			const radioInputs = form.querySelectorAll(
				`input[type="radio"][name="${name}"]`,
			);

			radioInputs.forEach((radio) => {
				const isTargetRadio = radio.value === value;
				const fieldset = radio.closest('[data-search-filter-form-tabbed]');
				const label = radio.closest('label');

				if (isTargetRadio && !radio.checked) {
					// Select the target radio
					radio.checked = true;

					// Update tabbed option styling
					if (fieldset && label) {
						label.classList.add('search-filter-form-tabbed__option--selected');

						// Remove selected class from other options in the same fieldset
						const otherLabels = fieldset.querySelectorAll(
							'label:not([for="' + radio.id + '"])',
						);
						otherLabels.forEach((otherLabel) => {
							otherLabel.classList.remove(
								'search-filter-form-tabbed__option--selected',
							);
						});
					}
				} else if (!isTargetRadio && radio.checked) {
					// Deselect other radios with the same name
					radio.checked = false;

					// Update tabbed option styling
					if (fieldset && label) {
						label.classList.remove(
							'search-filter-form-tabbed__option--selected',
						);
					}
				}
			});
		});
	}

	updateDropdownLabel(checkbox) {
		try {
			const dropdown = checkbox.closest(CONFIG.ELEMENTS.dropdown);
			if (!dropdown) return;

			const button = dropdown.querySelector(CONFIG.ELEMENTS.modalTrigger);
			if (!button) return;

			const label = button.querySelector(CONFIG.ELEMENTS.label);
			if (!label) return;

			const checkboxName = checkbox.name;
			const checkedOptions = dropdown.querySelectorAll(
				`input[name="${checkboxName}"]:checked`,
			);
			const checkedCount = checkedOptions.length;

			// Update the data-search-filter-form-label attribute for styling purposes
			if (checkedCount > 0) {
				label.setAttribute('data-search-filter-form-label', 'selected');
			} else {
				label.setAttribute('data-search-filter-form-label', '');
			}

			if (checkedCount === 0) {
				label.textContent = button.dataset.placeholder || 'Select option';
			} else if (checkedCount === 1) {
				const remainingCheckedOption = checkedOptions[0];
				const remainingSearchData = JSON.parse(
					remainingCheckedOption.dataset.searchData,
				);
				label.textContent = remainingSearchData.label;
			} else {
				// Multi-select
				const placeholder = button.dataset.placeholder || 'Options';
				label.textContent = `${placeholder} (${checkedCount})`;
			}

			if (checkedCount > 0) {
				this.validationManager.validateField(dropdown);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.warn('Failed to parse searchData:', error);
		}
	}
}
